import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast } from "sonner";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/jobs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="hidden md:block bp-hatch border-r-2 border-foreground p-10">
        <Link to="/" className="inline-flex items-center gap-4" data-testid="brand-link">
          <img src={BRAESIDE_LOGO_URL} alt="Braeside Plumbing and Drainage" className="h-14 w-auto" />
          <div className="h-9 w-px bg-foreground/40" />
          <div className="leading-tight text-left">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Onsite tool</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">Field Plan</div>
          </div>
        </Link>
        <div className="mt-24 max-w-md">
          <h2 className="font-heading font-black uppercase text-5xl leading-none">
            Back onsite.<br/>
            <span className="bg-[#FF5722] text-white px-2">Get planning.</span>
          </h2>
          <p className="mt-6 text-muted-foreground">Sign in to open your jobs, drop pins, sketch plans, and generate site reports.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md border-2 border-foreground bg-background p-8 shadow-block">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Step 01</div>
          <h1 className="font-heading font-black uppercase text-4xl mt-1">Sign in</h1>

          <label className="block mt-8 font-heading font-bold uppercase text-xs tracking-widest">Email</label>
          <input data-testid="login-email"
            type="email" required value={email} onChange={e => setEmail(e.target.value)}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <label className="block mt-4 font-heading font-bold uppercase text-xs tracking-widest">Password</label>
          <input data-testid="login-password"
            type="password" required value={password} onChange={e => setPassword(e.target.value)}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <button data-testid="login-submit" disabled={busy} className="btn-rugged mt-6 w-full">
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-foreground/20" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</div>
            <div className="flex-1 h-px bg-foreground/20" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton label="Continue with Google" testId="google-signin-login" />
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            No account? <Link data-testid="link-register" to="/register" className="underline underline-offset-4 font-bold">Create one</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
